interface ThresholdFetchParams {
  term: unknown;
  fetcher: (query: string) => void;
  minLength?: number;
  currentSuggestionsLength?: number;
}

/**
 * Checks if a given search term meets the minimum character length required
 * to initiate predictive search actions (defaulting to 3 or more characters).
 * * @param term - The search input to evaluate.
 * @param minLength - The minimum required length (default is 3).
 * @returns boolean - True if the term is a valid string meeting the minimum length.
 */
export const isValidSearchTerm = (
  term: unknown,
  minLength: number = 3
): term is string => {
  return typeof term === "string" && term.trim().length >= minLength;
};

/**
 * Executes a predictive search fetch function with threshold guarding.
 * If the term is valid and meets the threshold, it triggers the callback with the term.
 * If the term is invalid or too short, it clears the suggestions state by passing an empty string.
 * * @param term - The current search value from the input.
 * @param fetcher - The callback function to invoke with either the query string or an empty string.
 * @param minLength - The minimum characters required to trigger a query (default is 3).
 */
export const executeSearchWithThreshold = ({
  term,
  fetcher,
  minLength = 3,
  currentSuggestionsLength
}: ThresholdFetchParams): void => {
  const searchValue = typeof term === "string" ? term : "";
  if (isValidSearchTerm(searchValue, minLength)) {
    fetcher(searchValue);
  } else if (
    currentSuggestionsLength === undefined ||
    currentSuggestionsLength > 0
  ) {
    fetcher("");
  }
};
